/**
 * Governance Config Store — Persisted Policy Overrides
 *
 * RESOLVES: CONFLICT-03 (Governance Policy Settings Not Persisted)
 *
 * Stores governance policy overrides via dataLayer so that
 * GovernanceSettingsTab customizations survive page reloads.
 *
 * The policyEngine reads from this store at initialization,
 * merging overrides with defaultPolicy.
 *
 * Fase 2 dataLayer migration: localStorage calls replaced by dataLayer.
 */

import { debug, warn } from '../../utils/logger'
import { dataLayer } from '../../data/dataLayer'
import { STAGES } from './stageDefinitions'

// ─── Types ────────────────────────────────────────────────────────────────────

export interface GovernanceConfigOverrides {
  /** Schema version for migration safety */
  version: 1

  /** Quality gate overrides */
  qualityGates?: {
    minCogLevel?: number
    maxCogLevel?: number
    minDidacticDepth?: number
    minQualityScore?: number
  }

  /** Pipeline stage toggles */
  pipelineStages?: {
    schemaEnabled?: boolean
    typeEnabled?: boolean
    contentEnabled?: boolean
    supportEnabled?: boolean
    visualEnabled?: boolean
    graphEnabled?: boolean
    pedagogyEnabled?: boolean
  }

  /** Pipeline behavior */
  pipelineBehavior?: {
    strictMode?: boolean
    autoBlock?: boolean
  }

  /** Policy overrides */
  policy?: {
    minQuestionsPerSkill?: number
    maxQuestionsPerSkill?: number
    requireHints?: boolean
    requireExplanation?: boolean
    requireVisuals?: boolean
    distributionCheck?: boolean
  }

  /** Notification preferences */
  notifications?: {
    notifyBlockers?: boolean
    notifyWarnings?: boolean
    notifyWeeklyReport?: boolean
    notifyNewRules?: boolean
  }

  /** System settings */
  system?: {
    cacheEnabled?: boolean
    debugMode?: boolean
    autoValidateOnSave?: boolean
    batchSize?: number
  }

  /** Timestamp of last save */
  lastSaved?: number
}

// ─── Default Config ───────────────────────────────────────────────────────────

const DEFAULT_CONFIG: GovernanceConfigOverrides = {
  version: 1,
  qualityGates: {
    minCogLevel: 2,
    maxCogLevel: 5,
    minDidacticDepth: 2,
    minQualityScore: 70,
  },
  pipelineStages: {
    schemaEnabled: true,
    typeEnabled: true,
    contentEnabled: true,
    supportEnabled: true,
    visualEnabled: true,
    graphEnabled: true,
    pedagogyEnabled: true,
  },
  pipelineBehavior: {
    strictMode: false,
    autoBlock: true,
  },
  policy: {
    minQuestionsPerSkill: 5,
    maxQuestionsPerSkill: 50,
    requireHints: true,
    requireExplanation: true,
    requireVisuals: false,
    distributionCheck: true,
  },
  notifications: {
    notifyBlockers: true,
    notifyWarnings: false,
    notifyWeeklyReport: true,
    notifyNewRules: true,
  },
  system: {
    cacheEnabled: true,
    debugMode: false,
    autoValidateOnSave: true,
    batchSize: 100,
  },
}

// ─── Store Operations ─────────────────────────────────────────────────────────

/**
 * Load governance config from dataLayer.
 * Returns default config if nothing is stored or if stored data is invalid.
 */
export async function loadGovernanceConfig(): Promise<GovernanceConfigOverrides> {
  try {
    const parsed = await dataLayer.getGovernanceConfig()
    if (!parsed) return { ...DEFAULT_CONFIG }

    // Version check
    if (parsed.version !== 1) {
      warn('Governance config version mismatch, using defaults')
      return { ...DEFAULT_CONFIG }
    }

    // Merge with defaults to fill any missing fields
    return mergeWithDefaults(parsed as Partial<GovernanceConfigOverrides>)
  } catch (e) {
    warn('Failed to load governance config, using defaults')
    return { ...DEFAULT_CONFIG }
  }
}

/**
 * Save governance config via dataLayer.
 */
export async function saveGovernanceConfig(
  config: GovernanceConfigOverrides,
): Promise<boolean> {
  try {
    const toSave: GovernanceConfigOverrides = {
      ...config,
      version: 1,
      lastSaved: Date.now(),
    }
    const success = await dataLayer.saveGovernanceConfig(
      toSave as Record<string, unknown>,
    )
    if (success) {
      debug('✅ Governance config saved')
    }
    return success !== false
  } catch (e) {
    warn('Failed to save governance config')
    return false
  }
}

/**
 * Reset governance config to defaults via dataLayer.
 */
export async function resetGovernanceConfig(): Promise<GovernanceConfigOverrides> {
  await dataLayer.resetGovernanceConfig()
  debug('🔄 Governance config reset to defaults')
  return { ...DEFAULT_CONFIG }
}

/**
 * Get the default config (for reset operations).
 */
export function getDefaultGovernanceConfig(): GovernanceConfigOverrides {
  return { ...DEFAULT_CONFIG }
}

/**
 * Get the list of disabled pipeline stages from config.
 * Returns the subset of stage numbers (1-5) that should be skipped.
 *
 * Notes:
 *  - Stage 6 (scoring) and 7 (gate) are not skippable — they always run.
 *  - `pedagogyEnabled` is a sub-toggle of stage 3 and is NOT mapped here;
 *    consumers read it directly and pass it as `PipelineOptions.skipPedagogy`
 *    so the pedagogy sub-stage can be toggled without disabling content.
 *  - `typeEnabled` and `schemaEnabled` both share stage 1; toggling type
 *    alone is therefore a no-op while schema is on (by design — schema and
 *    type form the structural-validation pair).
 */
export function getDisabledStages(config: GovernanceConfigOverrides): number[] {
  const stages = config.pipelineStages
  if (!stages) return []

  const disabled: number[] = []
  if (stages.schemaEnabled === false) disabled.push(STAGES.SCHEMA)
  if (stages.graphEnabled === false) disabled.push(STAGES.GRAPH)
  if (stages.contentEnabled === false) disabled.push(STAGES.CONTENT)
  if (stages.visualEnabled === false) disabled.push(STAGES.VISUAL)
  if (stages.supportEnabled === false) disabled.push(STAGES.SUPPORT)
  // typeEnabled is part of stage 1 (schema+type pair)
  if (stages.typeEnabled === false) disabled.push(STAGES.TYPE)

  return [...new Set(disabled)]
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function mergeWithDefaults(
  stored: Partial<GovernanceConfigOverrides>,
): GovernanceConfigOverrides {
  return {
    version: 1,
    qualityGates: {
      ...DEFAULT_CONFIG.qualityGates,
      ...stored.qualityGates,
    },
    pipelineStages: {
      ...DEFAULT_CONFIG.pipelineStages,
      ...stored.pipelineStages,
    },
    pipelineBehavior: {
      ...DEFAULT_CONFIG.pipelineBehavior,
      ...stored.pipelineBehavior,
    },
    policy: {
      ...DEFAULT_CONFIG.policy,
      ...stored.policy,
    },
    notifications: {
      ...DEFAULT_CONFIG.notifications,
      ...stored.notifications,
    },
    system: {
      ...DEFAULT_CONFIG.system,
      ...stored.system,
    },
    lastSaved: stored.lastSaved,
  }
}
